import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { setBasic, setStep } from "../../reducers/userReducer";
import checkPhone from "../../controllers/account/checkPhone";
import checkEmail from "../../controllers/account/checkEmail";

function Input({
  value,
  onChange,
  onBlur,
  placeholder,
  type = "text",
  error,
  touched,
  loading,
  name,
}) {
  const showError = touched && !!error;

  return (
    <div className="relative">
      <input
        name={name}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        inputMode={name === "phoneNumber" ? "numeric" : undefined}
        pattern={name === "phoneNumber" ? "[0-9]*" : undefined}
        className={`w-full h-14 px-4 pr-10 rounded-md border outline-none placeholder:text-gray-400 text-gray-900 bg-white
          ${
            showError
              ? "border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-100"
              : "border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          }`}
      />

      {/* right-end loader */}
      {loading ? (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="primaryLoader"></div>
        </div>
      ) : null}

      {/* error text */}
      {showError ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
    </div>
  );
}

// --- helpers ---
const isEmail = (s) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s ?? "").trim());
const digitsOnly = (s) => String(s ?? "").replace(/\D/g, "");
const isPhone11 = (s) => String(s ?? "").length === 11; // we store digits-only
const hasName = (s) => String(s ?? "").trim().length > 0;

export default function ClaimBasicSection() {
  const user = useSelector((state) => state.user?.user);
  const dispatch = useDispatch();
  const basic = useSelector((s) => s.user.basic);

  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    email: false,
    phoneNumber: false,
  });

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
  });

  const [loading, setLoading] = useState({ email: false, phoneNumber: false });
  const [serverErr, setServerErr] = useState({ email: "", phoneNumber: "" });

  // Cancel token sources
  const emailCancelRef = useRef(null);
  const phoneCancelRef = useRef(null);

  // Debounce timers
  const emailDebounceRef = useRef(null);
  const phoneDebounceRef = useRef(null);

  const setFieldTouched = (field) =>
    setTouched((p) => ({ ...p, [field]: true }));

  // --- sync validate per field (no async here) ---
  const setFieldError = useCallback((field, msg) => {
    setErrors((p) => ({ ...p, [field]: msg }));
  }, []);

  const validateFieldSync = useCallback(
    (field, valueOverride) => {
      let msg = "";
      const { firstName, lastName, email, phoneNumber } = basic;

      switch (field) {
        case "firstName":
          if (!hasName(valueOverride ?? firstName))
            msg = "First name is required.";
          break;
        case "lastName":
          if (!hasName(valueOverride ?? lastName))
            msg = "Last name is required.";
          break;
        case "email":
          if (!isEmail(valueOverride ?? email))
            msg = "Enter a valid email address.";
          break;
        case "phoneNumber": {
          const v = valueOverride ?? phoneNumber;
          if (!isPhone11(v)) msg = "Phone must be 11 digits.";
          break;
        }
        default:
          break;
      }
      setFieldError(field, msg);
      return msg;
    },
    [basic, setFieldError]
  );

  // --- async uniqueness checks using CancelToken ---
  const validateEmailAsync = useCallback(
    async (val) => {
      // cancel previous
      if (emailCancelRef.current) {
        emailCancelRef.current.cancel("Request canceled.");
      }
      const source = axios.CancelToken.source();
      emailCancelRef.current = source;

      setLoading((p) => ({ ...p, email: true }));
      try {
        const res = await checkEmail(
          { email: val, accountId: user?._id || undefined },
          source.token
        );
        const exists = res?.return === 0 ?? false;

        // guard against race
        if (emailCancelRef.current !== source) return;

        setServerErr((p) => ({
          ...p,
          email: exists ? "Email already exists." : "",
        }));
      } catch (e) {
        if (axios.isCancel(e)) return;
        setServerErr((p) => ({ ...p, email: "Could not validate email." }));
      } finally {
        if (emailCancelRef.current === source) {
          setLoading((p) => ({ ...p, email: false }));
        }
      }
    },
    [user?._id]
  );

  const validatePhoneAsync = useCallback(
    async (valDigits) => {
      if (phoneCancelRef.current) {
        phoneCancelRef.current.cancel("Request canceled.");
      }
      const source = axios.CancelToken.source();
      phoneCancelRef.current = source;

      setLoading((p) => ({ ...p, phoneNumber: true }));
      try {
        const res = await checkPhone(
          { phone: valDigits, accountId: user?._id || undefined },
          source.token
        );
        const exists = res?.return === 0 ?? false;
        // console.log({ exists, res });

        if (phoneCancelRef.current !== source) return;

        setServerErr((p) => ({
          ...p,
          phoneNumber: exists ? "Phone already exists." : "",
        }));
      } catch (e) {
        if (axios.isCancel(e)) return;
        setServerErr((p) => ({
          ...p,
          phoneNumber: "Could not validate phone.",
        }));
      } finally {
        if (phoneCancelRef.current === source) {
          setLoading((p) => ({ ...p, phoneNumber: false }));
        }
      }
    },
    [user?._id]
  );

  // ---------- useEffect VALIDATION (debounced, no lag) ----------

  // PHONE: validate sync immediately, then (if clean) debounce async uniqueness
  useEffect(() => {
    const digits = basic.phoneNumber;
    // sync
    const msg = validateFieldSync("phoneNumber", digits);
    setServerErr((p) => ({ ...p, phoneNumber: "" })); // clear server error on change

    // async (only if sync OK and field looks complete)
    if (!msg && isPhone11(digits)) {
      // debounce 300ms
      clearTimeout(phoneDebounceRef.current);
      phoneDebounceRef.current = setTimeout(() => {
        validatePhoneAsync(digits);
      }, 300);
    } else {
      // cancel any in-flight & stop loader if invalid/incomplete
      if (phoneCancelRef.current)
        phoneCancelRef.current.cancel("Sync invalid.");
      setLoading((p) => ({ ...p, phoneNumber: false }));
    }

    return () => clearTimeout(phoneDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basic.phoneNumber]);

  // EMAIL: same pattern (optional but keeps behavior consistent)
  useEffect(() => {
    const val = String(basic.email ?? "").trim();
    const msg = validateFieldSync("email", val);
    setServerErr((p) => ({ ...p, email: "" }));

    if (!msg) {
      clearTimeout(emailDebounceRef.current);
      emailDebounceRef.current = setTimeout(() => {
        validateEmailAsync(val);
      }, 300);
    } else {
      if (emailCancelRef.current)
        emailCancelRef.current.cancel("Sync invalid.");
      setLoading((p) => ({ ...p, email: false }));
    }

    return () => clearTimeout(emailDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basic.email]);

  // --- blur only marks as touched; effects already validate ---
  const handleBlur = (field) => setFieldTouched(field);

  // --- update: write value; let effects validate (no lag) ---
  const update = (field) => (value) => {
    dispatch(
      setBasic({
        field,
        value: field === "phoneNumber" ? digitsOnly(value) : value,
      })
    );
    if (field === "email" || field === "phoneNumber") {
      setServerErr((p) => ({ ...p, [field]: "" }));
    }
  };

  // ------- Single gate: validateAll() on Continue -------
  const validateAll = useCallback(() => {
    const fields = ["firstName", "lastName", "email", "phoneNumber"];

    // mark touched
    setTouched((prev) => {
      const next = { ...prev };
      fields.forEach((f) => (next[f] = true));
      return next;
    });

    // run sync validations
    const syncErrors = {
      firstName: validateFieldSync("firstName"),
      lastName: validateFieldSync("lastName"),
      email: validateFieldSync("email"),
      phoneNumber: validateFieldSync("phoneNumber"),
    };

    const hasSyncErr = Object.values(syncErrors).some(Boolean);
    if (hasSyncErr) return false;

    // block if async checks still loading or server errors present
    if (loading.email || loading.phoneNumber) return false;
    if (serverErr.email || serverErr.phoneNumber) return false;

    return true;
  }, [
    loading.email,
    loading.phoneNumber,
    serverErr.email,
    serverErr.phoneNumber,
    validateFieldSync,
  ]);

  // compute invalid state ONLY for touched fields (for styling/disable)
  const fieldInvalid = (field) => {
    const syncErr = errors[field];
    const serverError = serverErr[field] || "";
    return touched[field] && (!!syncErr || !!serverError);
  };

  const anyInvalidTouched =
    ["firstName", "lastName", "email", "phoneNumber"].some((f) =>
      fieldInvalid(f)
    ) ||
    loading.email ||
    loading.phoneNumber;

  const anyTouched = Object.values(touched).some(Boolean);
  const disableButton = anyTouched ? anyInvalidTouched : false;

  const onContinue = (e) => {
    e.preventDefault();
    if (!validateAll()) return; // bounces only if invalid
    dispatch(setStep(2)); // proceed immediately if valid
  };

  return (
    <section className="w-full">
      {/* Title */}
      <div className="pt-5">
        <h2 className="text-xl font-semibold">Step 1: Basic Info</h2>
      </div>

      {/* Card */}
      <div className="rounded-lg bg-white border border-gray-200 p-4 my-4">
        <div className="space-y-4">
          <Input
            name="firstName"
            placeholder="Firstname"
            value={basic.firstName}
            onChange={update("firstName")}
            onBlur={() => handleBlur("firstName")}
            touched={touched.firstName}
            error={errors.firstName}
            loading={false}
          />
          <Input
            name="lastName"
            placeholder="Lastname"
            value={basic.lastName}
            onChange={update("lastName")}
            onBlur={() => handleBlur("lastName")}
            touched={touched.lastName}
            error={errors.lastName}
            loading={false}
          />
          <Input
            name="email"
            type="email"
            placeholder="Email"
            value={basic.email}
            onChange={update("email")}
            onBlur={() => handleBlur("email")}
            touched={touched.email}
            error={errors.email || serverErr.email}
            loading={loading.email}
          />
          <div>
            <Input
              name="phoneNumber"
              type="tel"
              placeholder="e.g 07012345678"
              value={basic.phoneNumber}
              onChange={update("phoneNumber")}
              onBlur={() => handleBlur("phoneNumber")}
              touched={touched.phoneNumber}
              error={errors.phoneNumber || serverErr.phoneNumber}
              loading={loading.phoneNumber}
            />
            <p className="mt-2 text-xs text-gray-500">
              A verification code will be sent to this number.
            </p>
          </div>
        </div>
      </div>

      <div className="py-5">
        <button
          type="submit"
          className={`w-full rounded-md py-3 text-white font-semibold bg-primary1 shadow-sm active:scale-[.99]
            ${disableButton ? "opacity-60 cursor-not-allowed" : ""}`}
          onClick={onContinue}
          disabled={disableButton}
        >
          Continue
        </button>
      </div>
    </section>
  );
}
